import { z } from 'zod';

import { menuCategoryTranslationsSchema } from '../menuCategory';
import { menuItemPublicSchema } from '../menuItem';
import { slugSchema, ulidSchema } from '../primitives';
import { cuisineSchema, fulfillmentTypeSchema, restaurantPublicSchema } from '../restaurant';

import { cursorListResponseSchema } from './_helpers';

const optionalTrimmedString = z
  .string()
  .optional()
  .transform((value) => {
    if (value === undefined) return undefined;
    const trimmed = value.trim();
    return trimmed.length === 0 ? undefined : trimmed;
  });

const optionalNumber = (label: string) =>
  z
    .string()
    .optional()
    .transform((value, ctx): number | undefined => {
      if (value === undefined || value.trim().length === 0) return undefined;
      const parsed = Number(value);
      if (!Number.isFinite(parsed)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${label} must be a number` });
        return z.NEVER;
      }
      return parsed;
    });

const optionalCuisineList = z
  .string()
  .optional()
  .transform((value, ctx): readonly z.infer<typeof cuisineSchema>[] | undefined => {
    if (value === undefined || value.trim().length === 0) return undefined;
    const parts = value
      .split(',')
      .map((part) => part.trim())
      .filter((part) => part.length > 0);
    const out: Array<z.infer<typeof cuisineSchema>> = [];
    for (const part of parts) {
      const result = cuisineSchema.safeParse(part);
      if (!result.success) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `unknown cuisine: ${part}` });
        return z.NEVER;
      }
      out.push(result.data);
    }
    return out;
  });

/**
 * Wire-format query schema for `GET /v1/restaurants`. Query keys are kebab-case per workspace
 * convention; the parsed object is camelCase.
 */
export const publicRestaurantsListWireQuerySchema = z
  .object({
    q: optionalTrimmedString,
    cuisine: optionalCuisineList,
    lat: optionalNumber('lat'),
    lng: optionalNumber('lng'),
    fulfillment: fulfillmentTypeSchema.optional(),
    'min-stars': optionalNumber('min-stars'),
    sort: z.enum(['distance', 'stars', 'review-count', 'relevance']).optional(),
    cursor: optionalTrimmedString,
    limit: optionalNumber('limit'),
  })
  .superRefine((value, ctx) => {
    const hasLat = value.lat !== undefined;
    const hasLng = value.lng !== undefined;
    if (hasLat !== hasLng) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'lat and lng must be provided together',
        path: hasLat ? ['lng'] : ['lat'],
      });
    }
    if (value['min-stars'] !== undefined) {
      const stars = value['min-stars'];
      if (!Number.isInteger(stars) || stars < 1 || stars > 5) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'min-stars must be an integer 1..5',
          path: ['min-stars'],
        });
      }
    }
    if (value.limit !== undefined) {
      const limit = value.limit;
      if (!Number.isInteger(limit) || limit < 1 || limit > 50) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'limit must be an integer 1..50',
          path: ['limit'],
        });
      }
    }
  })
  .transform((value) => ({
    text: value.q,
    cuisines: value.cuisine,
    lat: value.lat,
    lng: value.lng,
    fulfillment: value.fulfillment ?? ('PICKUP' as const),
    minStars: value['min-stars'],
    sort: value.sort ?? ('distance' as const),
    cursor: value.cursor,
    limit: value.limit ?? 20,
  }));

export type PublicRestaurantsListWireQuery = z.infer<typeof publicRestaurantsListWireQuerySchema>;

/** Client-facing discovery query (camelCase); serialized to kebab-case wire keys by the SDK. */
export const publicRestaurantsClientQuerySchema = z.object({
  text: z.string().optional(),
  cuisines: z.array(cuisineSchema).optional(),
  lat: z.number().finite().optional(),
  lng: z.number().finite().optional(),
  fulfillment: fulfillmentTypeSchema.optional(),
  minStars: z.number().int().min(1).max(5).optional(),
  sort: z.enum(['distance', 'stars', 'review-count', 'relevance']).optional(),
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(50).optional(),
});

export type PublicRestaurantsClientQuery = z.infer<typeof publicRestaurantsClientQuerySchema>;

export function serializePublicRestaurantsClientQuery(
  q: PublicRestaurantsClientQuery,
): Record<string, string | undefined> {
  const out: Record<string, string | undefined> = {};
  if (q.text !== undefined && q.text.trim().length > 0) out['q'] = q.text.trim();
  if (q.cuisines !== undefined && q.cuisines.length > 0) out['cuisine'] = q.cuisines.join(',');
  if (q.lat !== undefined) out['lat'] = String(q.lat);
  if (q.lng !== undefined) out['lng'] = String(q.lng);
  if (q.fulfillment !== undefined) out['fulfillment'] = q.fulfillment;
  if (q.minStars !== undefined) out['min-stars'] = String(q.minStars);
  if (q.sort !== undefined) out['sort'] = q.sort;
  if (q.cursor !== undefined && q.cursor.length > 0) out['cursor'] = q.cursor;
  if (q.limit !== undefined) out['limit'] = String(q.limit);
  return out;
}

export const publicRestaurantsListResponseSchema = cursorListResponseSchema(restaurantPublicSchema);
export type PublicRestaurantsListResponse = z.infer<typeof publicRestaurantsListResponseSchema>;

export const restaurantStatusMutationResponseSchema = z.object({
  restaurantId: ulidSchema,
  status: z.enum(['APPROVED', 'REJECTED', 'SUSPENDED']),
});
export type RestaurantStatusMutationResponse = z.infer<
  typeof restaurantStatusMutationResponseSchema
>;

/** Owner pause / resume — returns current restaurant `status` after the transition. */
export const restaurantOwnerLifecycleResponseSchema = z.object({
  restaurantId: ulidSchema,
  status: z.enum(['APPROVED', 'PAUSED']),
});
export type RestaurantOwnerLifecycleResponse = z.infer<
  typeof restaurantOwnerLifecycleResponseSchema
>;

export const menuCategoryPublicSchema = z.object({
  menuCategoryId: ulidSchema,
  name: z.string(),
  sortOrder: z.number().int(),
  translations: menuCategoryTranslationsSchema.optional(),
});
export type MenuCategoryPublic = z.infer<typeof menuCategoryPublicSchema>;

export const menuListResponseSchema = z.object({
  categories: z.array(menuCategoryPublicSchema),
  items: z.array(menuItemPublicSchema),
});
export type MenuListResponse = z.infer<typeof menuListResponseSchema>;

/** Path params for public restaurant routes — keyed by slug. */
export const restaurantPathParamsSchema = z.object({
  slug: slugSchema,
});
export type RestaurantPathParams = z.infer<typeof restaurantPathParamsSchema>;

/** Path params for admin/owner backoffice routes — keyed by ULID. */
export const restaurantIdPathParamsSchema = z.object({
  id: ulidSchema,
});
export type RestaurantIdPathParams = z.infer<typeof restaurantIdPathParamsSchema>;

export const publicDeliveryPricingTierSchema = z.object({
  minOrderValueMinorUnits: z.string().regex(/^(0|[1-9]\d*)$/),
  deliveryFeeMinorUnits: z.string().regex(/^(0|[1-9]\d*)$/),
});
export type PublicDeliveryPricingTier = z.infer<typeof publicDeliveryPricingTierSchema>;

export const publicDeliveryPricingResponseSchema = z.object({
  tiers: z.array(publicDeliveryPricingTierSchema),
});
export type PublicDeliveryPricingResponse = z.infer<typeof publicDeliveryPricingResponseSchema>;

export const reverseGeocodeQuerySchema = z.object({
  lat: z.string().transform((value, ctx) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'lat must be a number' });
      return z.NEVER;
    }
    return parsed;
  }),
  lng: z.string().transform((value, ctx) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'lng must be a number' });
      return z.NEVER;
    }
    return parsed;
  }),
});
export type ReverseGeocodeQuery = z.infer<typeof reverseGeocodeQuerySchema>;

export const geocodeAddressResponseSchema = z.object({
  address: z.string(),
  line1: z.string().optional(),
  city: z.string().optional(),
  country: z.string().optional(),
  lat: z.number(),
  lng: z.number(),
});
export type GeocodeAddressResponse = z.infer<typeof geocodeAddressResponseSchema>;

export const forwardGeocodeQuerySchema = z.object({
  q: z.string().min(3),
});
export type ForwardGeocodeQuery = z.infer<typeof forwardGeocodeQuerySchema>;

export const geocodeSuggestResponseSchema = z.object({
  suggestions: z.array(geocodeAddressResponseSchema),
});
export type GeocodeSuggestResponse = z.infer<typeof geocodeSuggestResponseSchema>;
